/**
 * Расчётное ядро графика (ПЛ-08, ПЛ-09, ПЛ-10): прямой и обратный проход по методу критического
 * пути, ранние и поздние даты, полный и свободный резерв, критический путь по резерву и по самой
 * длинной цепочке, почти критические работы, ограничения дат и их конфликты, расчёт от даты отчёта
 * с сохранением логики или приоритетом факта, несколько путей резерва.
 *
 * Ядро не зависит от базы и браузера: тот же код считает правку сразу в браузере и подтверждает
 * результат на сервере. Длительности, задержки и резервы — в рабочих минутах календаря работы.
 */
import { CALENDAR_24H, type CalendarDef, dayNumber, parseMoment, WorkCalendar } from './schedule-calendar';

export type ActivityType = 'task' | 'start_ms' | 'finish_ms' | 'loe' | 'hammock';
export type RelationType = 'FS' | 'SS' | 'FF' | 'SF';
export type ConstraintType = 'asap' | 'alap' | 'snet' | 'snlt' | 'fnet' | 'fnlt' | 'mso' | 'mfo';
export type LagCalendar = 'pred' | 'succ' | '24h' | 'project';

export interface EngineActivity {
  id: string;
  type: ActivityType;
  calendarId?: string | null;
  /** Плановая длительность, рабочих минут */
  duration: number;
  /** Оставшаяся длительность для начатой работы; по умолчанию — пропорционально проценту */
  remaining?: number | null;
  actualStart?: string | null;
  actualFinish?: string | null;
  /** Процент выполнения по длительности: для оставшейся длительности, если она не задана */
  percent?: number | null;
  constraint?: ConstraintType | null;
  constraintDate?: string | null;
  /** Крайний срок: ограничивает поздние даты, нарушение показывается */
  deadline?: string | null;
  /** Работа другого проекта: даты берутся как есть (ПЛ-05, связи между проектами) */
  external?: { start: string; finish: string } | null;
}

export interface EngineRelation {
  id: string;
  predId: string;
  succId: string;
  type: RelationType;
  /** Задержка (минус — опережение), рабочих минут календаря задержки */
  lag: number;
  /** Задержка в процентах длительности предшественника — вместо lag */
  lagPct?: number | null;
  lagCalendar?: LagCalendar | null;
}

export interface EngineOptions {
  /** Начало проекта: ранее него работы не начинаются */
  projectStart: string;
  /** Дата отчёта (дата данных): оставшаяся работа планируется не раньше неё */
  dataDate?: string | null;
  /** Директивное окончание проекта: поздние даты от него */
  mustFinishBy?: string | null;
  /** Прогресс вне последовательности: retained — сохранение логики, override — приоритет факта */
  outOfSequence?: 'retained' | 'override';
  /** Резерв от окончания проекта или каждая работа без последователей — своё окончание */
  floatFrom?: 'project' | 'open_ends';
  /** Полный резерв: по окончанию, по началу или наименьший */
  floatType?: 'finish' | 'start' | 'smallest';
  /** Календарь задержек по умолчанию */
  lagCalendar?: LagCalendar;
  /** Критический путь: по резерву не больше порога или по самой длинной цепочке */
  criticalBy?: 'float' | 'longest';
  criticalFloat?: number;
  /** Порог почти критических работ, рабочих минут */
  nearCriticalFloat?: number;
  defaultCalendarId: string;
  calendars: Record<string, CalendarDef>;
}

export interface ActivityResult {
  id: string;
  es: number;
  ef: number;
  ls: number;
  lf: number;
  /** Полный и свободный резерв; null — у выполненных работ и работ уровня усилий */
  tf: number | null;
  ff: number | null;
  /** Оставшаяся длительность после расчёта */
  remaining: number;
  critical: boolean;
  nearCritical: boolean;
  longestPath: boolean;
  /** Связь, определившая раннее начало (ведущая) */
  drivingRelationIds: string[];
  status: 'not_started' | 'in_progress' | 'complete';
}

export interface RelationResult {
  id: string;
  /** Резерв связи в рабочих минутах последователя; 0 — ведущая */
  slack: number;
  driving: boolean;
}

export interface Conflict {
  activityId: string;
  kind:
    | 'constraint_logic'
    | 'deadline'
    | 'negative_float'
    | 'cycle'
    | 'actual_after_data_date'
    | 'missing_relation_target';
  message: string;
}

export interface EngineResult {
  activities: Map<string, ActivityResult>;
  relations: Map<string, RelationResult>;
  projectStart: number;
  projectFinish: number;
  conflicts: Conflict[];
  /** Работы в циклах связей — для них связи цикла не учитываются */
  cycles: string[][];
  ms: number;
}

const NEG = Number.NEGATIVE_INFINITY;
const POS = Number.POSITIVE_INFINITY;

/** Горизонт календарей: от начала проекта за 2 года до 25 лет вперёд */
function horizon(acts: EngineActivity[], opts: EngineOptions): [number, number] {
  let lo = dayNumber(opts.projectStart);
  let hi = lo;
  const see = (s: string | null | undefined) => {
    if (!s) return;
    const d = dayNumber(s);
    if (d < lo) lo = d;
    if (d > hi) hi = d;
  };
  see(opts.dataDate);
  see(opts.mustFinishBy);
  for (const a of acts) {
    see(a.actualStart);
    see(a.actualFinish);
    see(a.constraintDate);
    see(a.deadline);
    see(a.external?.start);
    see(a.external?.finish);
  }
  const from = lo - 730;
  // Горизонт не больше 120 лет: даты дальше — ошибка ввода, а не план
  return [from, Math.min(hi - from + 365 * 25, 365 * 120)];
}

/** Кэш развёрнутых календарей одного расчёта */
export class CalendarSet {
  private readonly cache = new Map<string, WorkCalendar>();
  constructor(
    private readonly defs: Record<string, CalendarDef>,
    private readonly fromDay: number,
    private readonly days: number,
    private readonly fallback: string,
  ) {}
  get(id: string | null | undefined): WorkCalendar {
    const key = id && this.defs[id] ? id : id === '24h' ? '24h' : this.fallback;
    let c = this.cache.get(key);
    if (!c) {
      const def = key === '24h' ? CALENDAR_24H : (this.defs[key] ?? CALENDAR_24H);
      c = new WorkCalendar(def, this.fromDay, this.days);
      this.cache.set(key, c);
    }
    return c;
  }
}

export function makeCalendars(acts: EngineActivity[], opts: EngineOptions): CalendarSet {
  const [from, days] = horizon(acts, opts);
  return new CalendarSet(opts.calendars, from, days, opts.defaultCalendarId);
}

/**
 * Расчёт графика. Сложность — линейная от числа работ и связей (с логарифмом на перевод дат по
 * календарю): 10 000 работ и 30 000 связей считаются за доли секунды (ПЛ-11, НФ-02).
 */
export function computeSchedule(
  acts: EngineActivity[],
  rels: EngineRelation[],
  opts: EngineOptions,
  cals: CalendarSet = makeCalendars(acts, opts),
): EngineResult {
  const t0 = Date.now();
  const n = acts.length;
  const index = new Map<string, number>();
  for (let i = 0; i < n; i++) index.set(acts[i]!.id, i);
  const conflicts: Conflict[] = [];

  const cal = acts.map((a) => cals.get(a.calendarId));
  const projectCal = cals.get(opts.defaultCalendarId);
  const pStart = parseMoment(opts.projectStart);
  const dataDate = opts.dataDate ? parseMoment(opts.dataDate) : null;
  const lagDefault = opts.lagCalendar ?? 'pred';
  const override = opts.outOfSequence === 'override';
  const isLoe = acts.map((a) => a.type === 'loe' || a.type === 'hammock');

  // Связи по индексам; связи с несуществующей работой пропускаются
  type R = {
    id: string;
    p: number;
    s: number;
    type: RelationType;
    lag: number;
    pct: number | null;
    lc: LagCalendar;
  };
  const rr: R[] = [];
  for (const r of rels) {
    const p = index.get(r.predId);
    const s = index.get(r.succId);
    if (p === undefined || s === undefined) {
      const known = p ?? s;
      if (known !== undefined)
        conflicts.push({
          activityId: acts[known]!.id,
          kind: 'missing_relation_target',
          message: 'Связь с работой, которой нет в расчёте: пропущена',
        });
      continue;
    }
    if (p === s) continue;
    rr.push({
      id: r.id,
      p,
      s,
      type: r.type,
      lag: r.lag || 0,
      pct: r.lagPct ?? null,
      lc: r.lagCalendar ?? lagDefault,
    });
  }
  const predsOf: number[][] = Array.from({ length: n }, () => []);
  const succsOf: number[][] = Array.from({ length: n }, () => []);
  rr.forEach((r, k) => {
    predsOf[r.s]!.push(k);
    succsOf[r.p]!.push(k);
  });

  // Порядок расчёта (Кан); работа уровня усилий никого не двигает — её исходящие связи не в порядке
  const indeg = new Int32Array(n);
  for (const r of rr) if (!isLoe[r.p]) indeg[r.s]!++;
  const order: number[] = [];
  const queue: number[] = [];
  for (let i = 0; i < n; i++) if (indeg[i] === 0) queue.push(i);
  for (let qi = 0; qi < queue.length; qi++) {
    const i = queue[qi]!;
    order.push(i);
    if (isLoe[i]) continue;
    for (const k of succsOf[i]!) {
      const s = rr[k]!.s;
      if (--indeg[s]! === 0) queue.push(s);
    }
  }
  const cycles: string[][] = [];
  /** Номер цикла работы (сильно связной компоненты из двух и более работ), -1 — не в цикле */
  const cycleOf = new Int32Array(n).fill(-1);
  if (order.length < n) {
    const left: number[] = [];
    const isLeft = new Uint8Array(n);
    for (let i = 0; i < n; i++)
      if (indeg[i]! > 0) {
        left.push(i);
        isLeft[i] = 1;
      }
    // Тарьян без рекурсии: настоящие циклы, а не всё, что стоит после них
    const idx = new Int32Array(n).fill(-1);
    const low = new Int32Array(n);
    const onStack = new Uint8Array(n);
    const st: number[] = [];
    let counter = 0;
    const nextOf = (x: number) => (isLoe[x] ? [] : succsOf[x]!.map((k) => rr[k]!.s).filter((y) => isLeft[y]));
    for (const root of left) {
      if (idx[root]! >= 0) continue;
      const work: Array<{ v: number; it: number[]; k: number }> = [{ v: root, it: nextOf(root), k: 0 }];
      idx[root] = low[root] = counter++;
      st.push(root);
      onStack[root] = 1;
      while (work.length) {
        const fr = work[work.length - 1]!;
        if (fr.k < fr.it.length) {
          const w = fr.it[fr.k++]!;
          if (idx[w]! < 0) {
            idx[w] = low[w] = counter++;
            st.push(w);
            onStack[w] = 1;
            work.push({ v: w, it: nextOf(w), k: 0 });
          } else if (onStack[w]) low[fr.v] = Math.min(low[fr.v]!, idx[w]!);
          continue;
        }
        work.pop();
        const parent = work[work.length - 1];
        if (parent) low[parent.v] = Math.min(low[parent.v]!, low[fr.v]!);
        if (low[fr.v] === idx[fr.v]) {
          const comp: number[] = [];
          let x: number;
          do {
            x = st.pop()!;
            onStack[x] = 0;
            comp.push(x);
          } while (x !== fr.v);
          if (comp.length > 1) {
            const cid = cycles.length;
            cycles.push(comp.map((y) => acts[y]!.id));
            for (const y of comp) {
              cycleOf[y] = cid;
              conflicts.push({
                activityId: acts[y]!.id,
                kind: 'cycle',
                message: 'Работа в замкнутом цикле связей: связи внутри цикла не учитываются',
              });
            }
          }
        }
      }
    }
    // Оставшиеся работы — по порядку без связей внутри циклов
    const deg = new Int32Array(n);
    for (const r of rr)
      if (isLeft[r.s] && isLeft[r.p] && !isLoe[r.p] && !(cycleOf[r.p]! >= 0 && cycleOf[r.p] === cycleOf[r.s]))
        deg[r.s]!++;
    const q2 = left.filter((x) => deg[x] === 0);
    for (let qi = 0; qi < q2.length; qi++) {
      const x = q2[qi]!;
      order.push(x);
      if (isLoe[x]) continue;
      for (const k of succsOf[x]!) {
        const r = rr[k]!;
        if (!isLeft[r.s] || (cycleOf[x]! >= 0 && cycleOf[x] === cycleOf[r.s])) continue;
        if (--deg[r.s]! === 0) q2.push(r.s);
      }
    }
  }
  const sameCycle = (x: number, y: number) => cycleOf[x]! >= 0 && cycleOf[x] === cycleOf[y];

  const lagCal = (r: R) =>
    r.lc === '24h'
      ? cals.get('24h')
      : r.lc === 'succ'
        ? cal[r.s]!
        : r.lc === 'project'
          ? projectCal
          : cal[r.p]!;
  const dur = acts.map((a) =>
    a.type === 'start_ms' || a.type === 'finish_ms' ? 0 : Math.max(0, Math.round(a.duration || 0)),
  );
  const lagOf = (r: R) => (r.pct != null ? Math.round((dur[r.p]! * r.pct) / 100) : r.lag);
  /** Сдвиг момента на задержку по календарю задержки: вперёд — самый ранний момент */
  const shiftFwd = (t: number, lag: number, c: WorkCalendar) =>
    lag === 0 ? t : lag > 0 ? c.finishAt(c.workAt(t) + lag) : c.startAt(c.workAt(t) + lag);
  /** Назад — самый поздний момент */
  const shiftBack = (t: number, lag: number, c: WorkCalendar) =>
    lag === 0 ? t : lag > 0 ? c.startAt(c.workAt(t) - lag) : c.finishAt(c.workAt(t) - lag);

  const es = new Float64Array(n);
  const ef = new Float64Array(n);
  const ls = new Float64Array(n);
  const lf = new Float64Array(n);
  const rem = new Float64Array(n);
  const status: ActivityResult['status'][] = new Array(n);
  const isFinishMs = acts.map((a) => a.type === 'finish_ms');
  const cons = acts.map((a) => (a.constraint && a.constraintDate ? parseMoment(a.constraintDate) : null));
  const ext = acts.map((a) => Boolean(a.external));

  // Состояние и оставшаяся длительность
  for (let i = 0; i < n; i++) {
    const a = acts[i]!;
    if (a.actualFinish) status[i] = 'complete';
    else if (a.actualStart) status[i] = 'in_progress';
    else status[i] = 'not_started';
    if (status[i] === 'complete') rem[i] = 0;
    else if (status[i] === 'in_progress')
      rem[i] =
        a.remaining != null
          ? Math.max(0, Math.round(a.remaining))
          : // Остаток по проценту — с точностью до часа, как в отчётах площадки
            Math.round((dur[i]! * (1 - Math.min(100, Math.max(0, a.percent ?? 0)) / 100)) / 60) * 60;
    else rem[i] = a.remaining != null ? Math.max(0, Math.round(a.remaining)) : dur[i]!;
    if (dataDate !== null) {
      if (a.actualStart && parseMoment(a.actualStart) > dataDate)
        conflicts.push({
          activityId: a.id,
          kind: 'actual_after_data_date',
          message: 'Фактическое начало позже даты отчёта',
        });
      if (a.actualFinish && parseMoment(a.actualFinish) > dataDate)
        conflicts.push({
          activityId: a.id,
          kind: 'actual_after_data_date',
          message: 'Фактическое окончание позже даты отчёта',
        });
    }
  }

  /* ── Прямой проход ── */
  const drivingFwd: number[][] = Array.from({ length: n }, () => []);
  const floor = Math.max(pStart, dataDate ?? NEG);
  for (const i of order) {
    const a = acts[i]!;
    const c = cal[i]!;
    if (ext[i]) {
      es[i] = parseMoment(a.external!.start);
      ef[i] = parseMoment(a.external!.finish);
      continue;
    }
    // Ограничения от предшественников: на начало и на окончание
    let sc = status[i] === 'not_started' ? floor : NEG;
    let fc = NEG;
    for (const k of predsOf[i]!) {
      const r = rr[k]!;
      if (isLoe[r.p] || sameCycle(i, r.p)) continue;
      const anchor = r.type === 'FS' || r.type === 'FF' ? ef[r.p]! : es[r.p]!;
      const t = shiftFwd(anchor, lagOf(r), lagCal(r));
      if (r.type === 'FS' || r.type === 'SS') sc = Math.max(sc, t);
      else fc = Math.max(fc, t);
    }
    if (status[i] === 'complete') {
      es[i] = parseMoment(a.actualStart ?? a.actualFinish!);
      ef[i] = parseMoment(a.actualFinish!);
      continue;
    }
    if (status[i] === 'in_progress') {
      es[i] = parseMoment(a.actualStart!);
      // Остаток: не раньше даты отчёта; при сохранении логики — и не раньше предшественников
      let rs = dataDate ?? es[i]!;
      if (!override) rs = Math.max(rs, sc);
      rs = Math.max(rs, es[i]!);
      let w = c.workAt(rs) + rem[i]!;
      if (!override && fc > NEG) w = Math.max(w, c.workAt(fc));
      ef[i] = rem[i] === 0 && rs === es[i] ? c.finishAt(c.workAt(rs)) : c.finishAt(w);
      if (ef[i]! < es[i]!) ef[i] = es[i]!;
      continue;
    }
    const ct = a.constraint ?? 'asap';
    const cd = cons[i] ?? null;
    if (cd !== null) {
      if (ct === 'snet') sc = Math.max(sc, cd);
      if (ct === 'fnet') fc = Math.max(fc, cd);
    }
    if (sc === NEG) sc = floor;
    let ws = c.workAt(sc);
    let wf = ws + dur[i]!;
    if (fc > NEG && c.workAt(fc) > wf) {
      wf = c.workAt(fc);
      ws = wf - dur[i]!;
    }
    // Обязательные даты ломают логику: конфликт показывается
    if (cd !== null && (ct === 'mso' || ct === 'mfo')) {
      const want = ct === 'mso' ? c.workAt(cd) : c.workAt(cd) - dur[i]!;
      if (want < ws)
        conflicts.push({
          activityId: a.id,
          kind: 'constraint_logic',
          message:
            ct === 'mso'
              ? 'Обязательное начало раньше, чем позволяют связи'
              : 'Обязательное окончание раньше, чем позволяют связи',
        });
      ws = want;
      wf = ws + dur[i]!;
    }
    if (dur[i] === 0) {
      // Веха окончания — в конце рабочего времени, но не раньше, чем её разрешают связи и начало проекта
      let t = isFinishMs[i] ? c.finishAt(ws) : c.startAt(ws);
      if (t < sc) t = c.startAt(ws);
      es[i] = t;
      ef[i] = t;
    } else {
      es[i] = c.startAt(ws);
      ef[i] = c.finishAt(wf);
    }
  }

  // Работы уровня усилий и гамаки: от предшественников до последователей
  for (let i = 0; i < n; i++) {
    if (!isLoe[i] || status[i] === 'complete') continue;
    const c = cal[i]!;
    let fin = NEG;
    for (const k of succsOf[i]!) {
      const r = rr[k]!;
      const anchor = r.type === 'FS' || r.type === 'SS' ? es[r.s]! : ef[r.s]!;
      const t = shiftBack(anchor, lagOf(r), lagCal(r));
      if (r.type === 'FS' || r.type === 'FF') fin = Math.max(fin, t);
      else fin = Math.max(fin, c.finishAt(c.workAt(t) + dur[i]!));
    }
    if (fin > NEG) {
      ef[i] = Math.max(es[i]!, c.finishAt(c.workAt(fin)));
      rem[i] = c.workAt(ef[i]!) - c.workAt(Math.max(es[i]!, dataDate ?? es[i]!));
    }
  }

  // Окончание проекта — по работам, кроме уровня усилий
  let pFinish = NEG;
  let pFirst = POS;
  // Работы других проектов в сроки этого графика не входят
  for (let i = 0; i < n; i++) {
    if (ext[i]) continue;
    if (ef[i]! > pFinish && !isLoe[i]) pFinish = ef[i]!;
    if (es[i]! < pFirst) pFirst = es[i]!;
  }
  if (pFinish === NEG) pFinish = pStart;
  if (pFirst === POS) pFirst = pStart;
  const pLate = opts.mustFinishBy ? parseMoment(opts.mustFinishBy) : pFinish;
  const openEnds = opts.floatFrom === 'open_ends';

  /* ── Обратный проход ── */
  for (let oi = order.length - 1; oi >= 0; oi--) {
    const i = order[oi]!;
    const a = acts[i]!;
    const c = cal[i]!;
    if (ext[i] || status[i] === 'complete' || isLoe[i]) {
      ls[i] = es[i]!;
      lf[i] = ef[i]!;
      continue;
    }
    let lfc = POS;
    let lsc = POS;
    // Окончание без последователей по окончанию (ОН, ОО) открыто: его держит окончание проекта
    let finishSucc = false;
    for (const k of succsOf[i]!) {
      const r = rr[k]!;
      if (isLoe[r.s] || sameCycle(i, r.s)) continue;
      if (r.type === 'FS' || r.type === 'FF') finishSucc = true;
      const anchor = r.type === 'FS' || r.type === 'SS' ? ls[r.s]! : lf[r.s]!;
      const t = shiftBack(anchor, lagOf(r), lagCal(r));
      if (r.type === 'FS' || r.type === 'FF') lfc = Math.min(lfc, t);
      else lsc = Math.min(lsc, t);
    }
    if (!finishSucc) lfc = Math.min(lfc, openEnds ? ef[i]! : pLate);
    const ct = a.constraint ?? 'asap';
    const cd = cons[i] ?? null;
    if (cd !== null) {
      if (ct === 'snlt') lsc = Math.min(lsc, cd);
      if (ct === 'fnlt') lfc = Math.min(lfc, cd);
    }
    if (a.deadline) lfc = Math.min(lfc, parseMoment(a.deadline));
    const d = status[i] === 'in_progress' ? rem[i]! : dur[i]!;
    let wf = POS;
    if (lfc < POS) wf = c.workAt(lfc);
    if (lsc < POS) wf = Math.min(wf, c.workAt(lsc) + d);
    if (wf === POS) wf = c.workAt(ef[i]!);
    if (cd !== null && (ct === 'mso' || ct === 'mfo') && status[i] === 'not_started')
      wf = ct === 'mso' ? c.workAt(cd) + d : c.workAt(cd);
    if (d === 0) {
      const t = isFinishMs[i] ? c.finishAt(wf) : c.startAt(wf);
      ls[i] = t;
      lf[i] = t;
    } else {
      lf[i] = c.finishAt(wf);
      ls[i] = status[i] === 'in_progress' ? es[i]! : c.startAt(wf - d);
    }
    if (a.deadline && ef[i]! > parseMoment(a.deadline))
      conflicts.push({
        activityId: a.id,
        kind: 'deadline',
        message: 'Ранняя дата окончания позже крайнего срока',
      });
  }

  /* ── Резервы и ведущие связи ── */
  const tf = new Array<number | null>(n).fill(null);
  const ff = new Array<number | null>(n).fill(null);
  for (let i = 0; i < n; i++) {
    if (status[i] === 'complete' || isLoe[i] || ext[i]) continue;
    const c = cal[i]!;
    const fFloat = c.workAt(lf[i]!) - c.workAt(ef[i]!);
    const sFloat = status[i] === 'in_progress' ? fFloat : c.workAt(ls[i]!) - c.workAt(es[i]!);
    tf[i] =
      opts.floatType === 'start' ? sFloat : opts.floatType === 'smallest' ? Math.min(sFloat, fFloat) : fFloat;
    if (tf[i]! < 0)
      conflicts.push({
        activityId: acts[i]!.id,
        kind: 'negative_float',
        message: 'Отрицательный резерв: срок не выдерживается',
      });
  }
  const relOut = new Map<string, RelationResult>();
  for (let k = 0; k < rr.length; k++) {
    const r = rr[k]!;
    const cs = cal[r.s]!;
    const anchor = r.type === 'FS' || r.type === 'FF' ? ef[r.p]! : es[r.p]!;
    const t = shiftFwd(anchor, lagOf(r), lagCal(r));
    const target = r.type === 'FS' || r.type === 'SS' ? es[r.s]! : ef[r.s]!;
    const slack = cs.workAt(target) - cs.workAt(t);
    const driving =
      !isLoe[r.p] && !isLoe[r.s] && !sameCycle(r.p, r.s) && slack <= 0 && status[r.s] !== 'complete';
    relOut.set(r.id, { id: r.id, slack, driving });
    if (driving) drivingFwd[r.s]!.push(k);
    // Свободный резерв — в календаре предшественника
    if (!isLoe[r.s] && tf[r.p] !== null) {
      const cp = cal[r.p]!;
      const back = shiftBack(target, lagOf(r), lagCal(r));
      const own = r.type === 'FS' || r.type === 'FF' ? ef[r.p]! : es[r.p]!;
      const v = cp.workAt(back) - cp.workAt(own);
      ff[r.p] = ff[r.p] === null ? v : Math.min(ff[r.p]!, v);
    }
  }
  for (let i = 0; i < n; i++) {
    if (tf[i] === null) continue;
    const c = cal[i]!;
    if (ff[i] === null || !succsOf[i]!.some((k) => !isLoe[rr[k]!.s]))
      ff[i] = openEnds ? 0 : c.workAt(pLate) - c.workAt(ef[i]!);
    ff[i] = Math.max(0, Math.min(ff[i]!, Math.max(tf[i]!, 0)));
  }

  // «Как можно позже»: работа сдвигается на свободный резерв, последователей не двигает
  for (let i = 0; i < n; i++) {
    if (acts[i]!.constraint !== 'alap' || status[i] !== 'not_started' || !ff[i]) continue;
    const c = cal[i]!;
    const ws = c.workAt(es[i]!) + ff[i]!;
    if (dur[i] === 0) {
      const t = isFinishMs[i] ? c.finishAt(ws) : c.startAt(ws);
      es[i] = t;
      ef[i] = t;
    } else {
      es[i] = c.startAt(ws);
      ef[i] = c.finishAt(ws + dur[i]!);
    }
    ff[i] = 0;
  }

  // Самая длинная цепочка: от работ, заканчивающихся в окончание проекта, по ведущим связям назад
  const longest = new Uint8Array(n);
  const stack: number[] = [];
  for (let i = 0; i < n; i++)
    if (!isLoe[i] && !ext[i] && ef[i] === pFinish && status[i] !== 'complete') {
      longest[i] = 1;
      stack.push(i);
    }
  while (stack.length) {
    const i = stack.pop()!;
    for (const k of drivingFwd[i]!) {
      const p = rr[k]!.p;
      if (!longest[p] && !ext[p] && status[p] !== 'complete') {
        longest[p] = 1;
        stack.push(p);
      }
    }
  }

  const critFloat = opts.criticalFloat ?? 0;
  const nearFloat = opts.nearCriticalFloat ?? 0;
  const out = new Map<string, ActivityResult>();
  for (let i = 0; i < n; i++) {
    const critical =
      status[i] !== 'complete' &&
      !isLoe[i] &&
      (opts.criticalBy === 'longest' ? longest[i] === 1 : tf[i] !== null && tf[i]! <= critFloat);
    out.set(acts[i]!.id, {
      id: acts[i]!.id,
      es: es[i]!,
      ef: ef[i]!,
      ls: ls[i]!,
      lf: lf[i]!,
      tf: tf[i]!,
      ff: ff[i]!,
      remaining: rem[i]!,
      critical,
      nearCritical: !critical && tf[i] !== null && nearFloat > 0 && tf[i]! <= nearFloat,
      longestPath: longest[i] === 1,
      drivingRelationIds: drivingFwd[i]!.map((k) => rr[k]!.id),
      status: status[i]!,
    });
  }
  const t1 = Date.now();
  return {
    activities: out,
    relations: relOut,
    projectStart: pFirst,
    projectFinish: pFinish,
    conflicts,
    cycles,
    ms: Math.round(t1 - t0),
  };
}

/**
 * Несколько путей резерва к выбранной работе (ПЛ-09, как «Multiple Float Paths» в P6): первый путь —
 * ведущая цепочка к работе, следующие — цепочки с наименьшим резервом относительно уже найденных.
 */
export function floatPaths(
  acts: EngineActivity[],
  rels: EngineRelation[],
  res: EngineResult,
  targetId: string,
  maxPaths = 10,
): string[][] {
  const preds = new Map<string, EngineRelation[]>();
  for (const r of rels) {
    const l = preds.get(r.succId) ?? [];
    l.push(r);
    preds.set(r.succId, l);
  }
  const done = new Set<string>();
  const paths: string[][] = [];
  const trace = (start: string): string[] => {
    const path: string[] = [];
    let cur: string | undefined = start;
    while (cur && !done.has(cur)) {
      done.add(cur);
      path.push(cur);
      const drv: EngineRelation | undefined = (preds.get(cur) ?? [])
        .filter((r) => !done.has(r.predId) && res.activities.get(r.predId)?.status !== 'complete')
        .sort((a, b) => (res.relations.get(a.id)?.slack ?? 0) - (res.relations.get(b.id)?.slack ?? 0))[0];
      cur = drv && (res.relations.get(drv.id)?.slack ?? 1) <= 0 ? drv.predId : undefined;
    }
    return path.reverse();
  };
  if (!res.activities.has(targetId)) return [];
  paths.push(trace(targetId));
  while (paths.length < maxPaths) {
    // Кандидат: связь с уже найденным путём с наименьшим резервом связи
    let best: { id: string; slack: number } | null = null;
    for (const id of done) {
      for (const r of preds.get(id) ?? []) {
        if (done.has(r.predId) || res.activities.get(r.predId)?.status === 'complete') continue;
        const s = res.relations.get(r.id)?.slack ?? Number.POSITIVE_INFINITY;
        if (!best || s < best.slack) best = { id: r.predId, slack: s };
      }
    }
    if (!best) break;
    paths.push(trace(best.id));
  }
  void acts;
  return paths;
}

/* ───────────────────────── Длительность от объёма ───────────────────────── */

/**
 * Длительность работы от объёма и выработки (ПЛ-02): объём / (выработка звена в день × число звеньев),
 * округление вверх до целых часов; в рабочих минутах календаря работы.
 */
export function durationFromQuantity(
  quantity: number,
  ratePerDay: number,
  crews: number,
  hoursPerDay: number,
): number {
  if (!(quantity > 0) || !(ratePerDay > 0)) return 0;
  const days = quantity / (ratePerDay * Math.max(1, crews));
  return Math.ceil(days * hoursPerDay) * 60;
}
